import type { FC } from "react";
import { mergeClasses } from "@huub-web/utils";

import type { ActiveProp, ChildrenProp, EdgesProps } from "../../../props";
import { Edges } from "../../Edges/Edges";
import {
  checkboxStyle,
  edgeContentStyle,
  listOptionStyle,
} from "./SelectListItem.css";
import { Flex } from "../../Flex/Flex";
import type { SelectListOption, SelectListValue } from "../SelectList";
import { Checkbox } from "../../Checkbox/Checkbox";
import { Icon } from "../../Icon/Icon";

export type SelectListItemProps = EdgesProps &
  ChildrenProp &
  ActiveProp & {
    value: SelectListValue;
    selected?: boolean;
    multi?: boolean;
    variant?: SelectListOption["variant"];
    disabled?: boolean;
    onSelect: (value: SelectListValue) => void;
    onFocus?: () => void;
  };

/**
 * SelectListOption component is a dropdown component that allows users to select a single
 * option from a list of options.
 */
export const SelectListItem: FC<SelectListItemProps> = ({
  value,
  active,
  selected,
  leading,
  trailing,
  multi = false,
  variant = "default",
  disabled = false,
  onSelect,
  onFocus,
  children,
}) => {
  const handleOptionSelect = () => {
    if (disabled) {
      return;
    }
    onSelect(value);
  };

  return (
    <Flex
      as="li"
      tabIndex={-1}
      className={mergeClasses(
        listOptionStyle({
          selected,
          active,
          activeNotMulti: active && !multi,
          disabled,
          variant,
        }),
      )}
      {...(disabled && {
        "aria-disabled": true,
      })}
      onClick={handleOptionSelect}
      align="center"
      justify="center"
      data-value={value.toString()}
      onFocus={onFocus}
    >
      <Edges
        className={edgeContentStyle}
        leading={
          <>
            {multi && (
              <Checkbox
                className={checkboxStyle}
                checked={active}
                size="s"
                tabIndex={-1}
              />
            )}
            {leading}
          </>
        }
        trailing={
          <>
            {active && !multi && <Icon icon="checkThick" size="s" />}
            {trailing}
          </>
        }
      >
        {children}
      </Edges>
    </Flex>
  );
};
